import React, { FC, memo, useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { TableFilter, useTableFilterContext } from 'features/TableFilter';
import { WarehouseSelect } from './WarehouseSelect';
import { DepositsListFilterKeys } from '../../model/types';
import { RangeDatePicker } from 'shared/ui/RangeDatePicker/RangeDatePicker';
import { CLIENT_DATE_FORMAT } from 'shared/utils/constants';
import { DepositStatusSelect } from './DepositStatusSelect';
import { ApplicabilitySelect } from './ApplicabilitySelect';
import { DatePicker } from 'shared/ui/DatePicker';
import { AccountTypeSelect } from './AccountTypeSelect';

export const DepositListFilter: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'common']);

  const { filters, changeFilters, clearCurrentFilters } = useTableFilterContext<DepositsListFilterKeys>();

  const selectWarehouse = useCallback(
    (value: string[]) => {
      changeFilters({
        key: DepositsListFilterKeys.selectedWarehouses,
        label: `${t('Selected')}: ${value.length}`,
        value,
        title: t('Warehouse'),
      });
    },
    [changeFilters, t],
  );

  const changeCreationDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: DepositsListFilterKeys.creationDateFrom,
        label: t('From {{creationDateFrom}}', { creationDateFrom: clientFormatDateFrom }),
        value,
        title: t('Creation date'),
      });
    },
    [changeFilters, t],
  );

  const changeCreationDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: DepositsListFilterKeys.creationDateTo,
        label: t('To {{creationDateTo}}', { creationDateTo: clientFormatDateTo }),
        value,
        title: t('Creation date'),
      });
    },
    [changeFilters, t],
  );

  const changeInvoiceStatus = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: DepositsListFilterKeys.status, label, value, title: t('Status') });
    },
    [changeFilters, t],
  );

  const changeContractApplicability = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: DepositsListFilterKeys.isApplicable, label, value, title: t('Applicability') });
    },
    [changeFilters, t],
  );

  const changeStatusRefDate = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatStatusRefDate = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: DepositsListFilterKeys.statusRefDate,
        label: clientFormatStatusRefDate,
        value,
        title: t('statusRefDate'),
      });
    },
    [changeFilters, t],
  );

  const changeStartDateTo = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateTo = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: DepositsListFilterKeys.startDateTo,
        label: t('To {{startDateTo}}', { startDateTo: clientFormatDateTo }),
        value,
        title: t('Start date'),
      });
    },
    [changeFilters, t],
  );

  const changeStartDateFrom = useCallback(
    (value: Nullable<Dayjs>) => {
      const clientFormatDateFrom = dayjs(value).startOf('day').format(CLIENT_DATE_FORMAT);

      changeFilters({
        key: DepositsListFilterKeys.startDateFrom,
        label: t('From {{startDateFrom}}', { startDateFrom: clientFormatDateFrom }),
        value,
        title: t('Start date'),
      });
    },
    [changeFilters, t],
  );

  const changeAccountType = useCallback(
    (value: string, label: string) => {
      changeFilters({ key: DepositsListFilterKeys.accountType, label, value, title: t('Account type') });
    },
    [changeFilters, t],
  );

  return (
    <TableFilter>
      <div className="space-y-2">
        <WarehouseSelect values={filters.selectedWarehouses?.value} onChange={selectWarehouse} onClear={clearCurrentFilters} />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Creation range')}
          onChangeFrom={changeCreationDateFrom}
          onChangeTo={changeCreationDateTo}
          values={[
            filters.creationDateFrom?.value && dayjs(filters.creationDateFrom?.value),
            filters.creationDateTo?.value && dayjs(filters.creationDateTo?.value),
          ]}
        />

        <RangeDatePicker
          placeholder={[t('From'), t('To')]}
          label={t('Start contract range')}
          onChangeFrom={changeStartDateFrom}
          onChangeTo={changeStartDateTo}
          values={[
            filters.startDateFrom?.value && dayjs(filters.startDateFrom?.value),
            filters.startDateTo?.value && dayjs(filters.startDateTo?.value),
          ]}
        />

        <div className="flex space-x-3 items-center">
          <DepositStatusSelect value={filters.status?.value} onChange={changeInvoiceStatus} onClear={clearCurrentFilters} />

          <ApplicabilitySelect value={filters.isApplicable?.value} onChange={changeContractApplicability} onClear={clearCurrentFilters} />
        </div>

        <div className="flex space-x-3 items-center">
          <div className="flex-1">
            <div className="mb-2">{t('Status date')}</div>
            <DatePicker
              className="h-[52px] w-full"
              value={filters.statusRefDate?.value && dayjs(filters.statusRefDate?.value)}
              format={CLIENT_DATE_FORMAT}
              placeholder="statusRefDate"
              onChange={changeStatusRefDate}
            />
          </div>
          <AccountTypeSelect value={filters.accountType?.value} onChange={changeAccountType} onClear={clearCurrentFilters} />
        </div>
      </div>
    </TableFilter>
  );
});
